/**
 * スクリーンショット取得とアップロード
 *
 * html2canvas-pro を使用（oklch カラー関数対応）
 */

import html2canvas from 'html2canvas-pro';
import type { ScreenshotData } from './types';

/**
 * 画面のスクリーンショットを取得
 */
export async function captureScreenshot(): Promise<ScreenshotData> {
  const canvas = await html2canvas(document.body, {
    // オプション設定
    logging: false,
    useCORS: true,
    allowTaint: true,
    // スケールを下げてファイルサイズを抑える
    scale: 1,
    // 背景色を設定
    backgroundColor: '#ffffff',
  });

  const dataUrl = canvas.toDataURL('image/png');

  return { dataUrl };
}

/**
 * スクリーンショットをS3にアップロード
 */
export async function uploadScreenshot(
  dataUrl: string
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    // data URLからBlobを作成
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // Presigned URLを取得
    const presignResponse = await fetch('/api/debug/screenshot', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filename: `screenshot-${Date.now()}.png`,
        contentType: 'image/png',
      }),
    });

    if (!presignResponse.ok) {
      const error = await presignResponse.json();
      return { success: false, error: error.error || 'Failed to get upload URL' };
    }

    const presignData = await presignResponse.json();

    // S3にアップロード
    const formData = new FormData();
    Object.entries(presignData.fields as Record<string, string>).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', blob);

    const uploadResponse = await fetch(presignData.url, {
      method: 'POST',
      body: formData,
    });

    if (!uploadResponse.ok) {
      return { success: false, error: 'Failed to upload to S3' };
    }

    return { success: true, url: presignData.fileUrl };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * スクリーンショットをクリップボードにコピー
 */
export async function copyScreenshotToClipboard(dataUrl: string): Promise<boolean> {
  try {
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    await navigator.clipboard.write([
      new ClipboardItem({
        'image/png': blob,
      }),
    ]);

    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to copy screenshot to clipboard:', error);
    return false;
  }
}
